import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ArticleIcon from '@mui/icons-material/Article';
import AddBoxIcon from '@mui/icons-material/AddBox';
import RichTextCanvas, { RichTextCanvasRef } from '../canvas/RichTextCanvas';
import { SaveStatus } from '../canvas/SaveStateIndicator';
import { GnContext, GnContextValue, GnRole } from './GnContext';
import PageNavigator from './PageNavigator';
import { usePageView } from '../../hooks/usePageView';

export interface GnNotebookRef {
  canvas: () => RichTextCanvasRef | null;
}

interface GnNotebookProps {
  role: GnRole;
  courseId: number;
  /** null for inter-topic GNs (spec 046 FR-032). */
  nodeId: number | null;
  /** Pluggable persistence — tutor defaults (content_pages) when omitted. */
  loadFn?: () => Promise<string | null>;
  saveFn?: (blocks: string) => Promise<void>;
  onSaveStateChange?: (status: SaveStatus) => void;
  onPasteDetected?: (text: string, position: { top: number; left: number }) => void;
  onImagePasted?: (imageDataUrl: string, position: { top: number; left: number }) => void;
  onDocChanged?: (blocks: string) => void;
  /** Student topic GNs: last acknowledged publish timestamp + acknowledge callback (FR-039). */
  lastSeenPublishedAt?: string | null;
  onMarkPublishedSeen?: (publishedAt: string) => void;
  /** Bump when a new publish may exist (live-session seq change) — tutor region refetches. */
  publishRefreshKey?: number;
  /**
   * Canvas vs pages (spec 046 US3). Uncontrolled: GnNotebook owns the toggle internally,
   * seeded once from this initial value; onViewPrefChange is a fire-and-forget persistence
   * hook (e.g. the host PUTs it to the server) — it is never read back as a controlling prop.
   */
  initialViewPref?: 'CANVAS' | 'PAGES';
  onViewPrefChange?: (viewPref: 'CANVAS' | 'PAGES') => void;
  /** GN chrome mounted by the host page: publish button (tutor), voice control, cue layer. */
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Guruji NoteBook host (spec 046 FR-001/FR-006/FR-028..031): wraps RichTextCanvas with GN
 * context, pluggable persistence, and the canvas⇄pages view toggle. Pages are windowed by
 * hiding top-level DOM nodes outside the active page's range — the underlying document is
 * never altered by switching views (research Decision 1), only by an explicit convert action.
 */
const GnNotebook = forwardRef<GnNotebookRef, GnNotebookProps>(
  (
    {
      role,
      courseId,
      nodeId,
      loadFn,
      saveFn,
      onSaveStateChange,
      onPasteDetected,
      onImagePasted,
      onDocChanged,
      lastSeenPublishedAt = null,
      onMarkPublishedSeen,
      publishRefreshKey = 0,
      initialViewPref,
      onViewPrefChange,
      toolbar,
      children,
    },
    ref
  ) => {
    const canvasRef = useRef<RichTextCanvasRef>(null);
    const [seenPublishedAt, setSeenPublishedAt] = useState<string | null>(lastSeenPublishedAt);
    const [viewPref, setViewPrefState] = useState<'CANVAS' | 'PAGES'>(initialViewPref ?? 'CANVAS');
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [docVersion, setDocVersion] = useState(0);

    useImperativeHandle(ref, () => ({ canvas: () => canvasRef.current }), []);

    const getEditor = useCallback(() => canvasRef.current?.getEditor() ?? null, []);
    const { listPages, insertBreakAtCursor, convertToCanvas, getDoc } = usePageView(getEditor);

    const pages = useMemo(() => listPages(getDoc()), [listPages, getDoc, docVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    const setViewPref = (next: 'CANVAS' | 'PAGES') => {
      setViewPrefState(next);
      onViewPrefChange?.(next);
      if (next === 'CANVAS') setActivePageIndex(0);
    };

    const handleDocChanged = useCallback((blocks: string) => {
      setDocVersion(v => v + 1);
      onDocChanged?.(blocks);
    }, [onDocChanged]);

    // Window the visible content to the active page by hiding out-of-range top-level DOM
    // nodes — purely presentational; the document itself is untouched (FR-031).
    useEffect(() => {
      const editor = getEditor();
      if (!editor) return;
      const children = Array.from(editor.view.dom.children) as HTMLElement[];
      if (viewPref !== 'PAGES' || pages.length <= 1) {
        children.forEach(el => { el.style.display = ''; });
        return;
      }
      const page = pages[Math.min(activePageIndex, pages.length - 1)];
      const nextPage = pages[activePageIndex + 1];
      const start = page.startContentIndex;
      const end = nextPage ? nextPage.startContentIndex - 1 : children.length; // exclude the closing break itself
      children.forEach((el, i) => { el.style.display = i >= start && i < end ? '' : 'none'; });
    }, [viewPref, activePageIndex, pages, getEditor, docVersion]);

    const markPublishedSeen = useCallback(
      (publishedAt: string) => {
        setSeenPublishedAt(publishedAt);
        onMarkPublishedSeen?.(publishedAt);
      },
      [onMarkPublishedSeen]
    );

    const contextValue: GnContextValue = useMemo(
      () => ({
        role,
        courseId,
        nodeId,
        lastSeenPublishedAt: seenPublishedAt,
        markPublishedSeen,
        publishRefreshKey,
        viewPref,
      }),
      [role, courseId, nodeId, seenPublishedAt, markPublishedSeen, publishRefreshKey, viewPref]
    );

    const handleConvertToPages = () => {
      insertBreakAtCursor('New page');
      setViewPref('PAGES');
    };

    const handleAddPage = () => {
      const editor = getEditor();
      if (!editor) return;
      editor.chain().focus('end').insertContent({ type: 'pageBreak', attrs: { label: `Page ${pages.length + 1}` } }).run();
      setActivePageIndex(pages.length);
    };

    return (
      <GnContext.Provider value={contextValue}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="gn-notebook">
          {toolbar && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, pt: 1 }}>
              {toolbar}
              <Box sx={{ flex: 1 }} />
              {viewPref === 'CANVAS' ? (
                <Tooltip title="Convert to pages">
                  <IconButton size="small" onClick={handleConvertToPages} data-testid="gn-convert-to-pages">
                    <ViewAgendaIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Add page">
                    <IconButton size="small" onClick={handleAddPage} data-testid="gn-add-page">
                      <AddBoxIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Convert to single canvas">
                    <IconButton size="small" onClick={() => { convertToCanvas(); setViewPref('CANVAS'); }} data-testid="gn-convert-to-canvas">
                      <ArticleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          )}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {viewPref === 'PAGES' && (
              <PageNavigator pages={pages} activeIndex={activePageIndex} onJump={setActivePageIndex} />
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <RichTextCanvas
                ref={canvasRef}
                courseId={courseId}
                nodeId={nodeId ?? 0}
                loadFn={loadFn}
                saveFn={saveFn}
                onSaveStateChange={onSaveStateChange}
                onPasteDetected={onPasteDetected}
                onImagePasted={onImagePasted}
                onDocChanged={handleDocChanged}
              />
            </Box>
          </Box>
          {children}
        </Box>
      </GnContext.Provider>
    );
  }
);

GnNotebook.displayName = 'GnNotebook';

export default GnNotebook;
